import React, { useState, useEffect, useContext } from "react";
import { Button, Card, Col, Modal, Row, Tooltip, Typography } from "antd";
import {
  API_REQUEST_HEADERS_BASEURL,
  API_Fleet_ID_book,
  API_Fleet_ID_deleteBook,
  getFirebaseIdToken,
} from "../utils/Constants";
import UploadBookButton from "../components/uploadBookButton/UploadBookButton";
import { TbFileText, TbTrash } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase"; // Ajusta la ruta según tu proyecto
import { useGlobalToast } from "../context/ToastProvider";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const MAX_LIBRARY_BOOKS = 5;
const MAX_LIBRARY_FILE_SIZE = 10 * 1024 * 1024;

function isPdfFile(file) {
  const fileType = String(file?.type || "").trim().toLowerCase();
  const fileName = String(file?.name || "").trim().toLowerCase();
  return fileType === "application/pdf" || fileName.endsWith(".pdf");
}

function resolveLibraryUploadErrorMessage(error, t) {
  const code = String(error?.code || "").trim();
  const message = String(error?.message || "").trim();

  if (code === "storage/unauthorized" || message.includes("storage/unauthorized")) {
    return t("uploadBook.permissionDenied", {
      defaultValue: "You do not have permission to upload documents to this fleet library.",
    });
  }

  if (code === "storage/quota-exceeded" || message.includes("storage/quota-exceeded")) {
    return t("uploadBook.storageQuotaExceeded", {
      defaultValue: "Storage quota was exceeded. Please try again later.",
    });
  }

  return t("uploadBook.genericUploadError", {
    defaultValue: "An unexpected error occurred while uploading the document.",
  });
}

export default function Library() {
  const { getFleetID } = useContext(AuthContext);
  const fleetId = getFleetID();
  const primaryColor = "var(--chakra-colors-gandolapp-500)";
  const { t, i18n } = useTranslation();
  const toast = useGlobalToast();

  const [books, setBooks] = useState([]);
  const [bookToDelete, setBookToDelete] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const buildAuthRequestConfig = async () => {
    const token = await getFirebaseIdToken({ forceRefresh: true });
    return token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : undefined;
  };

  useEffect(() => {
    if (!fleetId) {
      setBooks([]);
      return undefined;
    }

    const libraryRef = collection(db, "fleets", fleetId, "library");
    const unsubscribe = onSnapshot(
      libraryRef,
      (snapshot) => {
        const realtimeBooks = snapshot.docs
          .map((bookDoc) => ({ id: bookDoc.id, ...bookDoc.data() }))
          .sort((a, b) => {
            const dateA =
              a?.timestamp?.toMillis?.() ||
              (a?.timestamp?.seconds ? a.timestamp.seconds * 1000 : new Date(a?.timestamp || 0).getTime());
            const dateB =
              b?.timestamp?.toMillis?.() ||
              (b?.timestamp?.seconds ? b.timestamp.seconds * 1000 : new Date(b?.timestamp || 0).getTime());
            return dateB - dateA;
          });
        setBooks(realtimeBooks);
      },
      (error) => {
        console.error("Error subscribing to library books:", error);
      }
    );

    return () => unsubscribe();
  }, [fleetId]);

  // Subir un nuevo libro
  const handleUpload = async (bookData) => {
    try {
      if (!fleetId) return false;
      setIsUploading(true);
      const file = bookData.file;

      if (!isPdfFile(file)) {
        toast({
          title: t("uploadBook.errorTitle"),
          description: t("uploadBook.invalidFile", { defaultValue: "Please choose a PDF document." }),
          status: "error",
        });
        return false;
      }

      if (file.size > MAX_LIBRARY_FILE_SIZE) {
        toast({
          title: t("uploadBook.maxSizeError"),
          description: t("uploadBook.maxSizeErrorDescription", { defaultValue: "Maximum size allowed 10 MB" }),
          status: "warning",
        });
        return false;
      }

      const ext = file.name.substring(file.name.lastIndexOf("."));
      const base = file.name.substring(0, file.name.lastIndexOf("."));
      const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, "");
      const uniqueFileName = `${base}_${timestamp}${ext}`;

      const storageRef = ref(storage, `fleets/${fleetId}/library/${uniqueFileName}`);
      await uploadBytes(storageRef, file, {
        contentType: "application/pdf",
      });

      const downloadURL = await getDownloadURL(storageRef);

      const fileLocation = storageRef.fullPath;

      const apiEndpoint = API_Fleet_ID_book.replace("{fleetId}", fleetId);
      const payload = {
        title: bookData.title,
        description: bookData.description,
        url: downloadURL,
        size: bookData.file.size,
        fileLocation,
      };

      const response = await API_REQUEST_HEADERS_BASEURL.post(
        apiEndpoint,
        payload,
        await buildAuthRequestConfig()
      );

      if (response.status === 201 || response.status === 200) {
        toast({
          title: t("uploadBook.successTitle"),
          description: t("uploadBook.successDescription"),
          status: "success",
        });
      }
      return true;
    } catch (error) {
      console.error("Error al subir el libro:", error);
      toast({ error,
        title: t("uploadBook.errorTitle", { defaultValue: "Upload failed" }),
        description: resolveLibraryUploadErrorMessage(error, t),
        status: "error",
      });
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  // Confirmar eliminación
  const confirmDelete = (book) => {
    setBookToDelete(book);
    setIsOpen(true);
  };

  // Eliminar libro
  const handleDeleteConfirmed = async () => {
    if (!bookToDelete) return;
    try {
      if (!fleetId) return;
      const apiEndpoint = API_Fleet_ID_deleteBook.replace("{fleetId}", fleetId).replace(
        "{bookId}",
        bookToDelete.id
      );

      await API_REQUEST_HEADERS_BASEURL.delete(apiEndpoint, await buildAuthRequestConfig());
    } catch (error) {
      console.error("Error al eliminar el libro:", error);
      toast({ error, title: t("feedback.documentDelete"), status: "error" });
    } finally {
      setBookToDelete(null);
      setIsOpen(false);
    }
  };

  const isUploadDisabled = books.length >= MAX_LIBRARY_BOOKS;

  // Formatear fecha
  const formatDate = (value) => {
    try {
      const localeTag = String(i18n?.resolvedLanguage || i18n?.language || "en").replace("_", "-");
      let date;
      if (value && typeof value.toDate === "function") {
        date = value.toDate();
      } else if (typeof value === "object" && (value?.seconds || value?._seconds)) {
        const seconds = value.seconds ?? value._seconds;
        date = new Date(seconds * 1000);
      } else {
        date = new Date(value);
      }
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString(localeTag, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch {
      return "";
    }
  };

  //Formatear tamaño de archivo
  const formatFileSize = (bytes) => {
    const num = parseInt(bytes, 10);
    if (isNaN(num)) return "";
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    return `${(num / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Abrir PDF en nueva pestaña
  const handleOpenPDF = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Tarjeta de libro
  const BookCard = ({ id, title, description, timestamp, url, size }) => (
    <Card
      hoverable
      onClick={() => handleOpenPDF(url)}
      style={{
        borderRadius: 16,
        textAlign: "center",
        position: "relative",
      }}
      bodyStyle={{ display: "grid", gap: 8, justifyItems: "center" }}
    >
      {size && (
        <span
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            fontSize: 11,
            color: "#667085",
            background: "#f2f4f7",
            padding: "2px 8px",
            borderRadius: 999,
          }}
        >
          {formatFileSize(size)}
        </span>
      )}
      <TbFileText size={40} color={primaryColor} />
      <Typography.Title level={4} style={{ color: primaryColor, margin: 0 }}>
        {title}
      </Typography.Title>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        📅 {formatDate(timestamp)}
      </Typography.Text>
      <Typography.Text type="secondary">{description}</Typography.Text>

      <Button
        type="text"
        icon={<TbTrash />}
        style={{ position: "absolute", top: 8, right: 8 }}
        onClick={(e) => {
          e.stopPropagation();
          confirmDelete({ id, title });
        }}
      />
    </Card>
  );

  return (
    <div style={{ minHeight: "100vh", padding: "48px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <Typography.Title level={2} style={{ color: primaryColor, marginBottom: 8 }}>
            {t("library.title")}
          </Typography.Title>
          <Typography.Text type="secondary">{t("library.subtitle")}</Typography.Text>
          <div style={{ marginTop: 8 }}>
            <Typography.Text type="secondary">
              {books.length} / {MAX_LIBRARY_BOOKS} {t("library.booksUploaded")}
            </Typography.Text>
          </div>
        </div>

        <Tooltip title={isUploadDisabled ? t("library.uploadLimitReached") : ""}>
          <div>
            <UploadBookButton onUpload={handleUpload} isDisabled={isUploadDisabled || isUploading} isUploading={isUploading} />
          </div>
        </Tooltip>
      </div>

      <Modal open={isUploading} footer={null} centered closable={false}>
        <Typography.Title level={5} style={{ marginBottom: 8 }}>
          {t("uploadBook.uploadingTitle", { defaultValue: "Uploading book..." })}
        </Typography.Title>
        <Typography.Text type="secondary">
          {t("uploadBook.uploadingSubtitle", {
            defaultValue: "This can take a minute while we process the PDF.",
          })}
        </Typography.Text>
      </Modal>

      {books.length > 0 ? (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          {books.map((book, index) => (
            <Col xs={24} md={8} key={book.id || `book-${index}`}>
              <BookCard
                id={book.id}
                title={book.title}
                description={book.description}
                timestamp={book.timestamp}
                url={book.url}
                size={book.size}
              />
            </Col>
          ))}
        </Row>
      ) : (
        <div style={{ marginTop: 32 }}>
          <Typography.Text type="secondary">{t("library.noBooks")}</Typography.Text>
        </div>
      )}

      <Modal
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        onOk={handleDeleteConfirmed}
        okText={t("library.delete")}
        cancelText={t("library.cancel")}
        centered
      >
        <Typography.Title level={5} style={{ marginTop: 8 }}>
          {t("library.confirmDeleteTitle")}
        </Typography.Title>
        <Typography.Paragraph>
          {t("library.confirmDeleteMessage", { title: bookToDelete?.title || "" })}
        </Typography.Paragraph>
      </Modal>
    </div>
  );
}
